import { US_STATE_CODES } from '../people/types';

export type FieldErrors<K extends string = string> = Partial<Record<K, string>>;

/** ABA routing number checksum: 3(d1+d4+d7) + 7(d2+d5+d8) + (d3+d6+d9) must be a multiple of 10. */
export function isValidAbaRoutingNumber(value: string): boolean {
  if (!/^\d{9}$/.test(value)) return false;
  const d = value.split('').map(Number) as [number, number, number, number, number, number, number, number, number];
  const sum = 3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8]);
  return sum % 10 === 0;
}

/** US phone: 10 digits (an optional leading 1 is tolerated). Returns the normalized digits or null when invalid. */
export function normalizeUsPhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return /^\d{10}$/.test(ten) ? ten : null;
}

export function formatPhoneForSave(raw: string): string {
  const ten = normalizeUsPhone(raw);
  return ten ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : raw.trim();
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isRealPastOrTodayDate(value: string, today: Date = new Date()): boolean {
  if (!ISO_DATE.test(value)) return false;
  const [y, m, day] = value.split('-').map(Number) as [number, number, number];
  const d = new Date(y, m - 1, day);
  if (d.getFullYear() !== y || d.getMonth() !== m - 1 || d.getDate() !== day) return false;
  if (y < 1900) return false;
  return value <= formatLocal(today);
}

function formatLocal(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// ---- Personal ----

export interface PersonalForm {
  firstName: string;
  lastName: string;
  phone: string;
  gender: string;
  dateOfBirth: string;
}
export type PersonalErrors = FieldErrors<keyof PersonalForm>;

export function validatePersonal(f: PersonalForm): PersonalErrors {
  const e: PersonalErrors = {};
  if (!f.firstName.trim()) e.firstName = 'First name is required.';
  if (!f.lastName.trim()) e.lastName = 'Last name is required.';
  if (!f.phone.trim()) e.phone = 'Phone number is required.';
  else if (!normalizeUsPhone(f.phone)) e.phone = 'Enter a 10-digit U.S. phone number.';
  if (!f.gender) e.gender = 'Gender is required.';
  if (!f.dateOfBirth) e.dateOfBirth = 'Date of birth is required.';
  else if (!isRealPastOrTodayDate(f.dateOfBirth)) e.dateOfBirth = 'Enter a valid date of birth that is not in the future.';
  return e;
}

export interface EmergencyForm {
  fullName: string;
  phone: string;
  relation: string;
}
export type EmergencyErrors = FieldErrors<keyof EmergencyForm>;

export const isEmergencyBlank = (f: EmergencyForm) => !f.fullName.trim() && !f.phone.trim() && !f.relation.trim();

/** Emergency contact is optional as a whole; once any part is entered, all three are needed. */
export function validateEmergency(f: EmergencyForm): EmergencyErrors {
  const e: EmergencyErrors = {};
  if (isEmergencyBlank(f)) return e;
  if (!f.fullName.trim()) e.fullName = 'Enter the contact’s full name.';
  if (!f.phone.trim()) e.phone = 'Enter the contact’s phone number.';
  else if (!normalizeUsPhone(f.phone)) e.phone = 'Enter a 10-digit U.S. phone number.';
  if (!f.relation.trim()) e.relation = 'Enter how this person is related to you.';
  return e;
}

// ---- Address ----

export interface AddressForm {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
}
export type AddressErrors = FieldErrors<keyof AddressForm>;
export const EMPTY_ADDRESS: AddressForm = { line1: '', line2: '', city: '', state: '', postalCode: '' };

export function validateAddress(f: AddressForm): AddressErrors {
  const e: AddressErrors = {};
  if (!f.line1.trim()) e.line1 = 'Address line 1 is required.';
  if (!f.city.trim()) e.city = 'City is required.';
  if (!f.state) e.state = 'State is required.';
  else if (!(US_STATE_CODES as readonly string[]).includes(f.state)) e.state = 'Choose a valid state.';
  if (!f.postalCode.trim()) e.postalCode = 'Zip code is required.';
  else if (!/^\d{5}(-\d{4})?$/.test(f.postalCode.trim())) e.postalCode = 'Use 12345 or 12345-6789.';
  return e;
}

// ---- Direct Deposit entry (client validation only; nothing is saved while the backend refuses it) ----

export interface BankForm {
  bankName: string;
  routingNumber: string;
  accountNumber: string;
}
export type BankErrors = FieldErrors<keyof BankForm>;

export function validateBank(f: BankForm): BankErrors {
  const e: BankErrors = {};
  if (!f.bankName.trim()) e.bankName = 'Bank name is required.';
  if (!f.routingNumber) e.routingNumber = 'Routing number is required.';
  else if (!/^\d{9}$/.test(f.routingNumber)) e.routingNumber = 'A routing number has exactly 9 digits.';
  else if (!isValidAbaRoutingNumber(f.routingNumber)) e.routingNumber = 'This routing number is not valid. Check the digits.';
  if (!f.accountNumber) e.accountNumber = 'Account number is required.';
  else if (!/^\d{4,17}$/.test(f.accountNumber)) e.accountNumber = 'An account number has 4 to 17 digits.';
  return e;
}
